import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const INDUSTRIES = [
  'Agriculture & Farming',
  'Automotive',
  'Banking & Financial Services',
  'Chemicals & Petrochemicals',
  'Construction & Real Estate',
  'Consumer Goods & Retail',
  'Education & Training',
  'Electronics & Electrical',
  'Energy & Utilities',
  'Engineering & Manufacturing',
  'Food & Beverages',
  'Healthcare & Pharmaceuticals',
  'Hospitality & Tourism',
  'Information Technology',
  'Logistics & Transportation',
  'Media & Entertainment',
  'Mining & Metals',
  'Oil & Gas',
  'Professional Services',
  'Telecommunications',
  'Textiles & Apparel',
  'Other',
];

const Signup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    industry: '',
    other_industry: '',
    password: '',
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Wait for Google SDK to be ready
  useEffect(() => {
    const checkGoogleReady = () => {
      if (window.google?.accounts?.id) {
        setGoogleReady(true);
      }
    };
    
    checkGoogleReady();
    const interval = setInterval(checkGoogleReady, 100);
    setTimeout(() => {
      clearInterval(interval);
      setGoogleReady(true);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Google login hook
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error('Failed to get user info from Google');
        }
        
        const userInfo = await userInfoResponse.json();
        
        const response = await fetch(`${BACKEND_URL}/api/auth/google/verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            credential: tokenResponse.access_token,
            user_info: userInfo
          }),
          credentials: 'include',
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.detail || 'Google sign-up failed');
        }

        if (data.access_token) {
          localStorage.setItem('token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          toast.success('Signed up with Google successfully!');
          navigate('/');
        }
      } catch (error) {
        console.error('Google signup error:', error);
        toast.error(error.message || 'Google sign-up failed');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error('Google Signup Error:', error);
      setGoogleLoading(false);
      if (error?.type === 'popup_closed') {
        toast.info('Sign-up cancelled');
        return;
      }
      toast.error('Google sign-up failed. Please try again or use email.');
    },
    flow: 'implicit',
  });

  const handleGoogleClick = () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    setTimeout(() => {
      googleLogin();
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.company_name.trim()) {
        throw new Error('Company name is required');
      }
      if (!formData.industry) {
        throw new Error('Please select an industry');
      }
      if (formData.industry === 'Other' && !formData.other_industry.trim()) {
        throw new Error('Please specify your industry');
      }

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        organization_name: formData.company_name,  // This creates the organization
        company_name: formData.company_name,
        industry: formData.industry === 'Other' ? formData.other_industry : formData.industry,
        password: formData.password,
      };

      const response = await fetch(`${BACKEND_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Signup failed');
      }

      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success('Account created successfully!');
        navigate('/');
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-8" data-testid="signup-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" data-testid="signup-title">
              Cloud RFQ
            </h1>
            <p className="text-sm text-slate-500">Create your account</p>
          </div>

          {/* Google Sign Up */}
          <div className="w-full mb-6" data-testid="google-signup-btn">
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 text-base font-medium border-slate-300 hover:bg-slate-50"
              onClick={handleGoogleClick}
              disabled={googleLoading || !googleReady}
            >
              {googleLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Signing up...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign up with Google
                </>
              )}
            </Button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-slate-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-slate-500">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  data-testid="signup-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  data-testid="signup-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                data-testid="signup-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                type="text"
                data-testid="signup-company-input"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                placeholder="Acme Corporation"
              />
            </div>

            <div>
              <Label htmlFor="industry">Type of Industry *</Label>
              <Select
                value={formData.industry}
                onValueChange={(value) => setFormData({ ...formData, industry: value })}
              >
                <SelectTrigger data-testid="signup-industry-select">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
                <SelectContent>
                  {INDUSTRIES.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.industry === 'Other' && (
              <div>
                <Label htmlFor="other_industry">Specify Industry *</Label>
                <Input
                  id="other_industry"
                  type="text"
                  data-testid="signup-other-industry-input"
                  value={formData.other_industry}
                  onChange={(e) => setFormData({ ...formData, other_industry: e.target.value })}
                  required
                  placeholder="Enter your industry"
                />
              </div>
            )}

            <div>
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                data-testid="signup-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="••••••••"
                minLength={6}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 6 characters</p>
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
              <UserPlus size={18} />
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-slate-900 hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
